import { useEffect, useState } from "react";
import useUser from "./useUser";
import useSelectedOrganization from "./useSelectedOrganization";
import { baseApiTest } from "api";

interface Usage {
    using: number,
    permit: number,
    can_use: boolean
}

interface SubscriptionFeatureData {
    ai_request_can_use: boolean;
    live_process_can_use: boolean;
    storage_disk_can_use: boolean;
    bypass_subscription: boolean;
    success: boolean;
    error?: string;
    uses: {
        live_process: Usage
        storage_disk: Usage
        ia_request: Usage
    },
}

interface UseSubscriptionFeaturesOptions {
    enabled?: boolean;
}

const useSubscriptionFeatures = ({
    enabled = true,
}: UseSubscriptionFeaturesOptions = {}) => {
    const user = useUser();
    const organization = useSelectedOrganization();
    const [canUseData, setCanUseData] = useState<SubscriptionFeatureData | null>(null);
    const [checkLoading, setCheckLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCanUseData = async () => {
        if (!enabled || !user?.jwt || !organization?.id) return;

        setCheckLoading(true);
        setError("");
        try {
            const response = await baseApiTest.get<SubscriptionFeatureData>(
                `organizations/${organization.id}/can-use`
            );
            setCanUseData(response.data);
        } catch (err) {
            setError("Errore nel recupero dei dati di sottoscrizione.");
        } finally {
            setCheckLoading(false);
        }
    };

    useEffect(() => {
        if (enabled) {
            fetchCanUseData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, organization?.id, user?.jwt]);

    return {
        checkLoading,
        error,
        aiRequestCanUse: canUseData?.ai_request_can_use,
        liveProcessCanUse: canUseData?.live_process_can_use,
        storageDiskCanUse: canUseData?.storage_disk_can_use,
        storageUsage: canUseData?.uses?.storage_disk,
        bypassSubscription: canUseData?.bypass_subscription,
        refreshSubscriptionFeatures: fetchCanUseData,
    };
};

export default useSubscriptionFeatures;
